import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
  Button, TextField, Box, List, ListItemButton, ListItemIcon, ListItemText,
  CircularProgress, Alert, IconButton, Typography,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import S3ApiService from '../../../services/s3-api.service';
import {
  ASSETS_ROOT, buildS3Key, joinLogical, parentLogical,
  s3KeyToLogical, stripTrailingSlash, getFileExtension,
} from './pathUtils';

export function NewFolderDialog({ open, onClose, onCreate, currentLogicalPath }) {
  const [name, setName] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);

  React.useEffect(() => {
    if (open) { setName(''); setErr(null); setBusy(false); }
  }, [open]);

  const submit = async () => {
    const clean = name.trim();
    if (!clean) return;
    if (/[\\/]/.test(clean)) { setErr('Folder name cannot contain slashes'); return; }
    setBusy(true); setErr(null);
    try {
      await onCreate(clean);
      onClose();
    } catch (e) {
      setErr(e.message || 'Failed to create folder');
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>New folder</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          In: {currentLogicalPath}
        </Typography>
        <TextField
          autoFocus fullWidth label="Folder name" value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          error={Boolean(err)} helperText={err || ' '}
          disabled={busy}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy} variant="outlined" color="inherit">Cancel</Button>
        <Button onClick={submit} variant="contained" disabled={busy || !name.trim()}>
          {busy ? <CircularProgress size={20} /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function RenameDialog({ open, item, onClose, onRename }) {
  const [name, setName] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);

  React.useEffect(() => {
    if (open && item) { setName(item.name || ''); setErr(null); setBusy(false); }
  }, [open, item]);

  if (!item) return null;
  const extChanged = item.type === 'file'
    && getFileExtension(item.name) !== getFileExtension(name);

  const submit = async () => {
    const clean = name.trim();
    if (!clean || clean === item.name) { onClose(); return; }
    if (/[\\/]/.test(clean)) { setErr('Name cannot contain slashes'); return; }
    setBusy(true); setErr(null);
    try {
      await onRename(item, clean);
      onClose();
    } catch (e) {
      setErr(e.message || 'Failed to rename');
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Rename {item.type}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus fullWidth label="New name" value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          error={Boolean(err)} helperText={err || ' '}
          disabled={busy}
        />
        {extChanged && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            You changed the file extension. The file will keep its current contents
            but other apps may stop opening it correctly.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy} variant="outlined" color="inherit">Cancel</Button>
        <Button onClick={submit} variant="contained" disabled={busy || !name.trim() || name === item.name}>
          {busy ? <CircularProgress size={20} /> : 'Rename'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function DeleteDialog({ open, items, onClose, onConfirm }) {
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  React.useEffect(() => { if (open) { setBusy(false); setErr(null); } }, [open]);

  if (!items || items.length === 0) return null;
  const folderCount = items.filter((i) => i.type === 'folder').length;
  const fileCount = items.length - folderCount;

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await onConfirm(items);
      onClose();
    } catch (e) {
      setErr(e.message || 'Failed to delete');
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Delete {items.length === 1 ? items[0].name : `${items.length} items`}?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {folderCount > 0
            ? `This will permanently delete ${folderCount} folder${folderCount === 1 ? '' : 's'} and everything inside${fileCount ? `, plus ${fileCount} file${fileCount === 1 ? '' : 's'}` : ''}.`
            : `This will permanently delete ${fileCount} file${fileCount === 1 ? '' : 's'}.`}
          {' '}This cannot be undone.
        </DialogContentText>
        {err && <Alert severity="error" sx={{ mt: 1 }}>{err}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy} variant="outlined" color="inherit">Cancel</Button>
        <Button onClick={submit} color="error" variant="contained" disabled={busy}>
          {busy ? <CircularProgress size={20} /> : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Move dialog: a folder picker that walks the explorer's own bucket so users
 * can choose the destination without leaving the page.
 */
export function MoveDialog({
  open, items, onClose, onConfirm,
  organizationName, sceneName, captionPathPrefix,
}) {
  const [cursor, setCursor] = React.useState(ASSETS_ROOT); // logical path
  const [folders, setFolders] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);

  React.useEffect(() => { if (open) { setCursor(ASSETS_ROOT); setBusy(false); setErr(null); } }, [open]);

  React.useEffect(() => {
    if (!open) return;
    setLoading(true);
    const key = buildS3Key({ organizationName, sceneName, captionPathPrefix, logicalPath: cursor });
    S3ApiService.listObjects({ prefix: `${key}/`, organizationName, sceneName, delimiter: '/' })
      .then((data) => {
        const list = (data?.folders || []).map((f) => ({
          name: (f.name || '').replace(/\/$/, ''),
          logicalPath: s3KeyToLogical({
            organizationName, sceneName, captionPathPrefix, fullKey: f.path,
          }),
        }));
        setFolders(list);
        setLoading(false);
      })
      .catch((e) => { setErr(e.message); setLoading(false); });
  }, [open, cursor, organizationName, sceneName, captionPathPrefix]);

  if (!items || items.length === 0) return null;

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await onConfirm(items, cursor);
      onClose();
    } catch (e) {
      setErr(e.message || 'Failed to move');
      setBusy(false);
    }
  };

  const movingPaths = new Set(items.map((i) => i.logicalPath));
  const cantMoveInto = (target) => {
    // can't move into self or descendant
    return items.some((it) => it.type === 'folder' && (target === it.logicalPath || target.startsWith(`${it.logicalPath}/`)));
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Move {items.length === 1 ? items[0].name : `${items.length} items`}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <IconButton size="small" onClick={() => setCursor(parentLogical(cursor))} disabled={cursor === ASSETS_ROOT}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {cursor}
          </Typography>
        </Box>
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 320, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>
          ) : (
            <List dense disablePadding>
              <ListItemButton onClick={() => setCursor(ASSETS_ROOT)}>
                <ListItemIcon><HomeIcon /></ListItemIcon>
                <ListItemText primary="Assets (root)" />
              </ListItemButton>
              {folders.length === 0 && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="caption" color="text.secondary">No subfolders here.</Typography>
                </Box>
              )}
              {folders.map((f) => {
                const disabled = movingPaths.has(f.logicalPath) || cantMoveInto(f.logicalPath);
                return (
                  <ListItemButton key={f.logicalPath} disabled={disabled}
                    onClick={() => setCursor(f.logicalPath)}>
                    <ListItemIcon><FolderIcon color="primary" /></ListItemIcon>
                    <ListItemText primary={f.name} secondary={disabled ? 'Cannot move here' : undefined} />
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Box>
        {err && <Alert severity="error" sx={{ mt: 1 }}>{err}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy} variant="outlined" color="inherit">Cancel</Button>
        <Button onClick={submit} variant="contained" disabled={busy || cantMoveInto(cursor)}>
          {busy ? <CircularProgress size={20} /> : `Move here`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
